import {memo, useContext} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import ActionsAdFavorite, {actionAdFavoriteTrackingOrigins, actionAdFavoriteTypes} from '@adv-mt/mt-actions-ad-favorite'
import {Tabs} from '@adv-mt/ui/tabs'

import Price from './price/Price.js'
import {BASE_CLASS as RENTING_BASE_CLASS} from './renting/config.js'
import RentingPrice from './renting/RentingPrice.js'
import Badges from './Badges.js'
import {BASE_CLASS} from './config.js'
import Heading from './Heading.js'

const PriceInfo = ({
  ad = {},
  adRentingForBusiness,
  extendedTrackingData = {},
  financingData,
  isFavorite,
  isPersonalLoanAvailable,
  optionalEquipment = [],
  targetBuyer,
  onOpenDrawer,
  onSelectionChange,
  onTargetBuyerChange
}) => {
  const {i18n, domain} = useContext(Context)
  const config = domain.get('config')

  const {IS_PRICE_DROP_BUTTON_ENABLED} = config.get('favorites')
  const {TARGET_BUYER_IDS} = config.get('subscription')
  const priceSectionId = config.get('COMMON').AD_DETAIL_PAGE_SECTIONS.AD_DETAIL_PAGE_SECTION_IDS.PRICE

  // Controlled by the page: the same selection also drives the contact form's
  // target buyer buttons and travels on the lead, so it cannot live here.
  const activeTargetBuyer = targetBuyer || TARGET_BUYER_IDS.PRIVATES

  const isBusinessTargetBuyer = activeTargetBuyer === TARGET_BUYER_IDS.BUSINESS
  const displayedAd = isBusinessTargetBuyer && adRentingForBusiness ? adRentingForBusiness : ad

  const isRentingAd = Boolean(displayedAd.monthlySubscriptionFee)

  const showPriceDropCta = IS_PRICE_DROP_BUTTON_ENABLED && !isRentingAd
  const showTargetBuyerTabs = isRentingAd && Boolean(adRentingForBusiness)

  const badges = (
    <Badges
      ad={displayedAd}
      className={isRentingAd ? `${RENTING_BASE_CLASS}-badges` : `${BASE_CLASS}-badges`}
      isBusinessTargetBuyer={isBusinessTargetBuyer}
      isRentingAd={isRentingAd}
      optionalEquipment={optionalEquipment}
    />
  )

  return (
    <section id={priceSectionId} className={BASE_CLASS}>
      <div className={`${BASE_CLASS}-vehicleInfo`}>
        <Heading ad={displayedAd} isRentingAd={isRentingAd} />

        {showTargetBuyerTabs ? (
          <Tabs.Root className={`${BASE_CLASS}-prices`} onValueChange={onTargetBuyerChange} value={activeTargetBuyer}>
            <Tabs.List>
              <Tabs.Tab className={`${BASE_CLASS}-tab`} value={TARGET_BUYER_IDS.PRIVATES}>
                {i18n.t('COMMON.RENTING_PRIVATE')}
              </Tabs.Tab>
              <Tabs.Tab className={`${BASE_CLASS}-tab`} value={TARGET_BUYER_IDS.BUSINESS}>
                {i18n.t('COMMON.RENTING_BUSINESS')}
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value={activeTargetBuyer}>
              <RentingPrice
                ad={displayedAd}
                badges={badges}
                onOpenDrawer={onOpenDrawer}
                onSelectionChange={onSelectionChange}
              />
            </Tabs.Panel>
          </Tabs.Root>
        ) : (
          <div className={`${BASE_CLASS}-prices`}>
            {isRentingAd ? (
              <RentingPrice
                ad={displayedAd}
                badges={badges}
                onOpenDrawer={onOpenDrawer}
                onSelectionChange={onSelectionChange}
              />
            ) : (
              <>
                <Price
                  ad={displayedAd}
                  financingData={financingData}
                  isPersonalLoanAvailable={isPersonalLoanAvailable}
                  onOpenDrawer={onOpenDrawer}
                />
                {badges}
              </>
            )}
          </div>
        )}
      </div>

      {showPriceDropCta && (
        <ActionsAdFavorite
          ad={ad}
          extendedTrackingData={extendedTrackingData}
          isFavorite={isFavorite}
          trackingOrigin={actionAdFavoriteTrackingOrigins.DETAIL_PRICE_DROP_AD_FAVORITED}
          type={actionAdFavoriteTypes.PRICE_DROP_INLINE_BUTTON}
        />
      )}
    </section>
  )
}

PriceInfo.displayName = 'PriceInfo'
PriceInfo.propTypes = {
  ad: PropTypes.object,
  // getInitialProps resolves this to `false` when the ad has no subscriptionBusinessAdInfo
  adRentingForBusiness: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  extendedTrackingData: PropTypes.object,
  financingData: PropTypes.shape({
    computedPrice: PropTypes.number,
    entry: PropTypes.number,
    fee: PropTypes.number,
    financedPrice: PropTypes.number,
    minEntry: PropTypes.number
  }),
  isFavorite: PropTypes.bool,
  isPersonalLoanAvailable: PropTypes.bool,
  optionalEquipment: PropTypes.array,
  targetBuyer: PropTypes.string,
  onOpenDrawer: PropTypes.func.isRequired,
  onSelectionChange: PropTypes.func,
  onTargetBuyerChange: PropTypes.func
}

export default memo(PriceInfo)
